/**
 * Overview.jsx — Aba "Visão geral" do DetailPanel.
 *
 * Conteúdo:
 *   - 4 mini-KPIs (vendas hoje/7d, estoque Bling, ML disponível)
 *   - Last8DaysChart (8 colunas, comparação dia da semana com hover %diff)
 *   - SparkChart 30 dias (visualização macro mensal)
 *   - PrevisaoFormula (melhor7×7 + média7×15)
 *   - PerformanceTable (média/melhor/previsão/cobertura/vendas30)
 *
 * Tudo lê dados do row via Context (useDetailPanel).
 */
import { useState } from 'react';
import { TKPI } from '../../primitives';
import { SparkChart } from '../../SparkChart';
import { useDetailPanel } from '../index.jsx';
import { CHART_HEIGHT } from '../internals.jsx';

/**
 * Gráfico de barras dos últimos 8 dias terminando em HOJE. 8 colunas exatas
 * (não 7) pra permitir comparação dia da semana → mesmo dia da semana anterior
 * (sex passada → sex hoje, etc).
 *
 * Hover comportamento: quando o user passa mouse numa coluna, todas as outras
 * recebem um label acima mostrando o % de diferença vs. a hovered. Coluna
 * hovered mostra "100%" (base de comparação). Verde se cresceu, vermelho se
 * caiu, cinza se zero.
 */
function Last8DaysChart({ row, T }) {
    const [hoveredIdx, setHoveredIdx] = useState(null);
    // Slice 8 dias (hoje no fim) — usamos dailyAll porque row.daily só tem 7.
    // Fallback pra row.daily se dailyAll não existir (compat).
    const all = Array.isArray(row.dailyAll) ? row.dailyAll : Array.isArray(row.daily) ? row.daily : [];
    let days = all.slice(-8);
    // Pad com zeros se faltar dado — SEMPRE 8 colunas, mesmo pra SKUs sem
    // vendas (antes ficava null/escondido pros sem daily, criando UX desigual
    // entre SKUs no painel). Hoje sempre fica no fim (idx 7).
    if (days.length < 8) {
        days = [...new Array(8 - days.length).fill(0), ...days];
    }

    const max = Math.max(...days, 1);
    const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    // Anchor UTC vindo do row (asOf do servidor) — sem `new Date()` local.
    const today = typeof row.anchorDateIso === 'string' ? new Date(`${row.anchorDateIso}T00:00:00Z`) : new Date();
    const hoveredVal = hoveredIdx !== null ? days[hoveredIdx] : null;

    return (
        <>
            <div
                style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    color: T.muted,
                    marginBottom: 8,
                }}
            >
                Vendas últimos 8 dias
            </div>
            {/* biome-ignore lint/a11y/noStaticElementInteractions: hover é só visual (mostra %diff). Sem ação ao clicar; teclado não precisa interagir. */}
            <div
                style={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-end',
                    height: 100,
                    marginBottom: 8,
                }}
                onMouseLeave={() => setHoveredIdx(null)}
            >
                {days.map((v, i) => {
                    const daysAgo = days.length - 1 - i;
                    const date = new Date(today);
                    date.setUTCDate(today.getUTCDate() - daysAgo);
                    const dayLabel = WEEKDAYS[date.getUTCDay()];
                    const isLast = i === days.length - 1;
                    const isoKey = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

                    // %diff label: só renderiza se o user está fazendo hover em ALGUMA
                    // coluna (qualquer). A hovered mostra "·" como referência.
                    let diffLabel = '';
                    let diffColor = T.muted;
                    if (hoveredIdx !== null) {
                        if (i === hoveredIdx) {
                            diffLabel = '·';
                            diffColor = T.ink;
                        } else if (hoveredVal === 0) {
                            // Divisão por zero: mostra diff absoluto como ∞ ou texto.
                            diffLabel = v > 0 ? '+∞' : '0%';
                            diffColor = v > 0 ? T.ok : T.muted;
                        } else {
                            const pct = ((v - hoveredVal) / hoveredVal) * 100;
                            const sign = pct > 0 ? '+' : '';
                            diffLabel = `${sign}${pct.toFixed(0)}%`;
                            diffColor = pct > 0 ? T.ok : pct < 0 ? T.warn : T.muted;
                        }
                    }

                    return (
                        // biome-ignore lint/a11y/noStaticElementInteractions: hover decorativo só pra %diff. Sem click handler, não precisa role.
                        <div
                            key={`${row.sku}-${isoKey}`}
                            onMouseEnter={() => setHoveredIdx(i)}
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 3,
                                cursor: 'default',
                            }}
                        >
                            {/* Label de diff acima da coluna — altura fixa pra não pular o layout. */}
                            <div
                                style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: diffColor,
                                    fontFamily: T.mono,
                                    height: 12,
                                    lineHeight: 1,
                                }}
                            >
                                {diffLabel}
                            </div>
                            <div
                                style={{
                                    width: '100%',
                                    background: hoveredIdx === i ? T.ink : isLast ? T.accent : T.accentBg,
                                    height: `${(v / max) * 64}px`,
                                    borderRadius: '2px 2px 0 0',
                                    minHeight: v > 0 ? 3 : 0,
                                    transition: 'background 0.1s',
                                }}
                            />
                            <div style={{ fontSize: 10, color: T.muted, fontFamily: T.mono }}>{v}</div>
                            <div
                                style={{
                                    fontSize: 9,
                                    color: isLast ? T.accent : T.subtle,
                                    fontWeight: isLast ? 600 : 400,
                                }}
                            >
                                {dayLabel}
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
}

/**
 * Previsão: mostra a fórmula explícita usada pra estimar demanda de 15 dias.
 *   forecast = melhor_dia_7d × 7 + média_7d × 15
 * Cada termo é renderizado isoladamente com o valor entre parênteses pro usuário
 * conseguir auditar visualmente como o número final foi montado.
 */
function PrevisaoFormula({ row, T }) {
    // Arredondamos PRIMEIRO o avg pra 1 casa decimal (mesma precisão usada no
    // display "(165.3)") e SÓ DEPOIS multiplicamos. Sem isso o user via
    // display "165.3 × 15 = 2479.3" porque o real era 165.286... × 15 =
    // 2479.29... — números no display batiam com o cálculo interno.
    //
    // O total é computado AQUI também (não vem mais via prop) pra garantir
    // que `partBest + partAvg` seja exatamente igual ao total mostrado.
    const best = Math.round(row.best7 || 0);
    const avg = Math.round(Number(row.avg7 || 0) * 10) / 10;
    const partBest = best * 7;
    const partAvg = avg * 15;
    const total = partBest + partAvg;
    const formatDecimal = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
    return (
        <div style={{ marginBottom: 16 }}>
            <div
                style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    color: T.muted,
                    marginBottom: 6,
                }}
            >
                Previsão de demanda 15d
            </div>
            <div
                style={{
                    border: `1px solid ${T.line}`,
                    borderRadius: 4,
                    padding: '12px 14px',
                    background: T.bgSoft,
                    fontFamily: T.mono,
                    fontSize: 13,
                    color: T.ink,
                    lineHeight: 1.7,
                }}
            >
                <div>
                    melhor dia 7d <span style={{ color: T.muted }}>({best})</span>{' '}
                    <span style={{ color: T.muted }}>×</span> 7 <span style={{ color: T.muted }}>=</span>{' '}
                    {formatDecimal(partBest)}
                </div>
                <div>
                    média 7d <span style={{ color: T.muted }}>({formatDecimal(avg)})</span>{' '}
                    <span style={{ color: T.muted }}>×</span> 15 <span style={{ color: T.muted }}>=</span>{' '}
                    {formatDecimal(partAvg)}
                </div>
                <div
                    style={{
                        borderTop: `1px solid ${T.line}`,
                        marginTop: 8,
                        paddingTop: 8,
                        fontSize: 14,
                        fontWeight: 600,
                    }}
                >
                    total <span style={{ color: T.muted }}>=</span> {formatDecimal(total)} un.
                </div>
            </div>
        </div>
    );
}

/** Tabela simples de métricas curtas. Cada linha = um KPI calculável. */
function PerformanceTable({ row, forecast, coverage, coverageColor, T }) {
    const rows = [
        ['média 7d', `${(row.avg7 || 0).toFixed(1)} un/dia`],
        ['melhor dia 7d', `${row.best7 || 0} un`],
        ['previsão 15d', `${forecast} un`],
        ['cobertura', `${coverage} dias`, coverageColor],
        ['vendas 30d', `${row.sales30 || 0} un`],
    ];
    return (
        <div>
            <div
                style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    color: T.muted,
                    marginBottom: 6,
                }}
            >
                Performance
            </div>
            <div
                style={{
                    border: `1px solid ${T.line}`,
                    borderRadius: 4,
                    overflow: 'hidden',
                }}
            >
                {rows.map(([k, v, c], i) => (
                    <div
                        key={k}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            fontSize: 13,
                            borderBottom: i < rows.length - 1 ? `1px solid ${T.line}` : 'none',
                            background: i % 2 === 0 ? T.bg : T.bgSoft,
                        }}
                    >
                        <span style={{ color: T.muted }}>{k}</span>
                        <span style={{ fontFamily: T.mono, fontWeight: 600, color: c || T.ink }}>{v}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function Overview() {
    const { row, T, tab, forecast, coverage, coverageColor } = useDetailPanel();
    // Compound API: cada tab decide se renderiza com base no tab ativo.
    if (tab !== 'overview') return null;
    return (
        <div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 8,
                    marginBottom: 16,
                }}
            >
                <TKPI label="Vendas hoje" value={row.sales_today || 0} mini />
                <TKPI label="Vendas 7d" value={row.sales7 || 0} mini />
                <TKPI
                    label="Estoque Bling"
                    value={row.bling || 0}
                    mini
                    color={(row.bling || 0) < (row.low_stock_threshold ?? 20) ? 'warn' : undefined}
                />
                <TKPI
                    label="Mercado Livre disponível"
                    value={row.mlAvail || 0}
                    mini
                    color={(row.mlAvail || 0) === 0 ? 'warn' : undefined}
                />
            </div>

            {/* Last8DaysChart movido de Vendas — visão geral merece o gráfico
                mais informativo (8 dias com hover %diff). "Tendência 7 dias"
                removida pois era redundante. */}
            <Last8DaysChart row={row} T={T} />

            {/* Sparkline 30 dias (também movido de Vendas) — visualização
                macro da tendência mensal. */}
            <div style={{ marginTop: 16, marginBottom: 16 }}>
                <div
                    style={{
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        color: T.muted,
                        marginBottom: 8,
                    }}
                >
                    Últimos 30 dias
                </div>
                <SparkChart
                    values={row.daily30 || []}
                    height={CHART_HEIGHT}
                    color={T.accent}
                    fallbackLength={30}
                    anchorDate={row.anchorDateIso}
                />
            </div>

            {/* === Previsão: fórmula explícita === */}
            <PrevisaoFormula row={row} T={T} />

            {/* === Performance: tabela simples de métricas curtas === */}
            <PerformanceTable row={row} forecast={forecast} coverage={coverage} coverageColor={coverageColor} T={T} />
        </div>
    );
}
